import { TimelineEvent } from '../models/TimelineEvent';

export interface GroupedTimelineEvents {
  dateLabel: string;
  dateKey: string;
  events: TimelineEvent[];
}

/**
 * Format date for compact display (e.g., "Dec 15" or "Today", "Yesterday")
 */
export function formatCompactDate(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Reset time for comparison
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

  if (dateOnly.getTime() === todayOnly.getTime()) {
    return 'Today';
  }
  if (dateOnly.getTime() === yesterdayOnly.getTime()) {
    return 'Yesterday';
  }

  // Check if within last 7 days
  const daysDiff = Math.floor((todayOnly.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));
  if (daysDiff <= 7 && daysDiff > 0) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }

  // Check if same year
  if (date.getFullYear() === today.getFullYear()) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  // Include year if different year
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Format date and time for compact display
 */
export function formatCompactDateTime(dateStr: string, timeStr?: string): string {
  const dateFormatted = formatCompactDate(dateStr);
  if (timeStr) {
    // Format time to 12-hour format without seconds
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${dateFormatted} at ${hour12}:${minutes} ${ampm}`;
  }
  return dateFormatted;
}

/**
 * Group timeline events by date
 */
export function groupEventsByDate(events: TimelineEvent[]): GroupedTimelineEvents[] {
  const groups = new Map<string, TimelineEvent[]>();

  events.forEach((event) => {
    const dateKey = new Date(event.startDate).toDateString();
    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(event);
  });

  // Convert to array and sort by date (newest first)
  return Array.from(groups.entries())
    .map(([dateKey, events]) => ({
      dateKey,
      dateLabel: formatCompactDate(events[0].startDate),
      events: events.sort((a, b) => {
        // Sort events within same day by time (if available) or by creation order
        if (a.startTime && b.startTime) {
          return b.startTime.localeCompare(a.startTime);
        }
        return 0;
      })
    }))
    .sort((a, b) => {
      const dateA = new Date(a.events[0].startDate);
      const dateB = new Date(b.events[0].startDate);
      return dateB.getTime() - dateA.getTime();
    });
}

